import { Lock } from 'lucide-react'
import { BANK_DETAILS_PATH, COMPANY_PROFILE_PATH } from '@/lib/masterDataLock'

/** A one-line hint under a locked block: where its values come from and where to change them. */
export function LockedHint({ source }: { source: 'company' | 'bank' }) {
  const href = source === 'company' ? COMPANY_PROFILE_PATH : BANK_DETAILS_PATH
  const label = source === 'company' ? 'Company Profile' : 'Bank Details'
  return (
    <p className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
      <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>
        Managed in Settings &rarr;{' '}
        <a href={href} target="_blank" rel="noopener noreferrer" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
          {label}
        </a>
      </span>
    </p>
  )
}

/** Shown when the company or bank details a document needs are empty; saving is blocked until they are filled in Settings. */
export function MasterDataMissingBanner({ company, bank }: { company: string[]; bank: string[] }) {
  if (company.length === 0 && bank.length === 0) return null
  return (
    <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
      <p className="font-semibold">Company or bank details are incomplete, so this document can't be saved yet.</p>
      <ul className="mt-1 list-disc pl-5 text-xs">
        {company.length > 0 && (
          <li>
            Company Profile is missing: {company.join(', ')}.{' '}
            <a href={COMPANY_PROFILE_PATH} target="_blank" rel="noopener noreferrer" className="font-medium underline">
              Open Company Profile
            </a>
          </li>
        )}
        {bank.length > 0 && (
          <li>
            Bank details are missing: {bank.join(', ')}. Choose a bank account above, or add one in{' '}
            <a href={BANK_DETAILS_PATH} target="_blank" rel="noopener noreferrer" className="font-medium underline">
              Bank Details
            </a>
          </li>
        )}
      </ul>
      <p className="mt-1 text-xs">After you save them in Settings, reopen this form.</p>
    </div>
  )
}
